import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { defaultThemeKey, LANE_CHOOSER_THEME_KEYS } from '@/features/laneChooser/types';

import type { ReactNode } from 'react';
import type { LaneChooserTheme } from '@/features/laneChooser/types';

export { defaultThemeKey, LANE_CHOOSER_THEME_KEYS } from '@/features/laneChooser/types';
export type { LaneChooserTheme } from '@/features/laneChooser/types';

const themeLoaders: Record<LANE_CHOOSER_THEME_KEYS, () => Promise<{ default: LaneChooserTheme }>> = {
	[LANE_CHOOSER_THEME_KEYS.LAKE]: () => import('@/features/laneChooser/themes/lake'),
	[LANE_CHOOSER_THEME_KEYS.FOREST]: () => import('@/features/laneChooser/themes/forest'),
	[LANE_CHOOSER_THEME_KEYS.MOUNTAIN]: () => import('@/features/laneChooser/themes/mountain'),
	[LANE_CHOOSER_THEME_KEYS.DESERT]: () => import('@/features/laneChooser/themes/desert'),
	[LANE_CHOOSER_THEME_KEYS.PARK]: () => import('@/features/laneChooser/themes/park'),
};

const themeCache = new Map<LANE_CHOOSER_THEME_KEYS, LaneChooserTheme>();
const themePromiseCache = new Map<LANE_CHOOSER_THEME_KEYS, Promise<LaneChooserTheme>>();
const LaneChooserThemeContext = createContext<LaneChooserTheme | null>(null);

type LaneChooserThemeState = {
	resolvedKey: LANE_CHOOSER_THEME_KEYS | null;
	theme: LaneChooserTheme | null;
};

export const normalizeLaneChooserThemeKey = (key?: string | null): LANE_CHOOSER_THEME_KEYS => {
	const normalizedKey = key?.trim().toLowerCase();

	switch (normalizedKey) {
		case LANE_CHOOSER_THEME_KEYS.FOREST:
			return LANE_CHOOSER_THEME_KEYS.FOREST;
		case LANE_CHOOSER_THEME_KEYS.MOUNTAIN:
			return LANE_CHOOSER_THEME_KEYS.MOUNTAIN;
		case LANE_CHOOSER_THEME_KEYS.DESERT:
			return LANE_CHOOSER_THEME_KEYS.DESERT;
		case LANE_CHOOSER_THEME_KEYS.PARK:
			return LANE_CHOOSER_THEME_KEYS.PARK;
		case LANE_CHOOSER_THEME_KEYS.LAKE:
		default:
			return defaultThemeKey;
	}
};

const getInitialThemeState = (key: LANE_CHOOSER_THEME_KEYS): LaneChooserThemeState => {
	const cachedTheme = themeCache.get(key) ?? null;

	return {
		resolvedKey: cachedTheme ? key : null,
		theme: cachedTheme,
	};
};

const loadLaneChooserTheme = async (key: LANE_CHOOSER_THEME_KEYS): Promise<LaneChooserTheme> => {
	const cachedTheme = themeCache.get(key);
	if (cachedTheme) {
		return cachedTheme;
	}

	const pendingTheme = themePromiseCache.get(key);
	if (pendingTheme) {
		return pendingTheme;
	}

	const themePromise = themeLoaders[key]()
		.then((themeModule) => {
			themeCache.set(key, themeModule.default);
			return themeModule.default;
		})
		.finally(() => {
			themePromiseCache.delete(key);
		});

	themePromiseCache.set(key, themePromise);
	return themePromise;
};

export const getLaneChooserTheme = async (key?: string | null): Promise<LaneChooserTheme> => {
	const normalizedKey = normalizeLaneChooserThemeKey(key);

	try {
		return await loadLaneChooserTheme(normalizedKey);
	} catch (error) {
		if (normalizedKey === defaultThemeKey) {
			throw error;
		}

		console.error(
			`[LaneChooserTheme] Failed to load "${normalizedKey}". Falling back to "${defaultThemeKey}".`,
			error,
		);

		return loadLaneChooserTheme(defaultThemeKey);
	}
};

export const useLaneChooserTheme = (): LaneChooserTheme => {
	const theme = useContext(LaneChooserThemeContext);
	if (!theme) {
		throw new Error('useLaneChooserTheme must be used within LaneChooserThemeProvider');
	}

	return theme;
};

type LaneChooserThemeProviderProps = {
	themeKey?: string | null;
	children: ReactNode;
};

export const LaneChooserThemeProvider = ({ themeKey, children }: LaneChooserThemeProviderProps) => {
	const normalizedThemeKey = useMemo(() => normalizeLaneChooserThemeKey(themeKey), [themeKey]);
	const [themeState, setThemeState] = useState<LaneChooserThemeState>(() => getInitialThemeState(normalizedThemeKey));

	useEffect(() => {
		let isActive = true;
		setThemeState(getInitialThemeState(normalizedThemeKey));

		getLaneChooserTheme(normalizedThemeKey)
			.then((loadedTheme) => {
				if (isActive) {
					setThemeState({
						resolvedKey: normalizedThemeKey,
						theme: loadedTheme,
					});
				}
			})
			.catch((error) => {
				console.error(`[LaneChooserTheme] Failed to load fallback theme "${defaultThemeKey}".`, error);
			});

		return () => {
			isActive = false;
		};
	}, [normalizedThemeKey]);

	if (!themeState.theme || themeState.resolvedKey !== normalizedThemeKey) {
		return null;
	}

	return <LaneChooserThemeContext.Provider value={themeState.theme}>{children}</LaneChooserThemeContext.Provider>;
};
